import { ref } from 'vue';
import { useGlobalStore } from '@/stores/globalStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { getOrderData, sendConversionTrackEvent } from '@/composables/api/useTracker';

const isProduction = import.meta.env.VITE_ENV === 'production';

const checkIfProdTestingMerchant = () => {
  const testingMerchantList = ['spa', 'cremorne'];
  const globalStore = useGlobalStore();
  const currentMerchant = globalStore.getMerchantSettings?.name?.toLowerCase();
  return testingMerchantList.includes(currentMerchant);
};

const safeLog = (actionName, payload) => {
  const isProdTestingMerchant = checkIfProdTestingMerchant();
  // Track in production for testing merchants
  if (isProduction && !isProdTestingMerchant) { return; }
  try {
    // eslint-disable-next-line no-console
    console.log('[DD RUM]', actionName, payload);
  } catch {
    // ignore console failures
  }
};

/** Strips common PII keys for debug display and flow payloads (same rules as DD context). */
const sanitize = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  const out = { ...obj };
  delete out.email;
  delete out.phone;
  delete out.first_name;
  delete out.last_name;
  delete out.full_name;
  delete out.address;
  return out;
};

// -----------------------------------------------------------------------------
// Dev-only: conversion debug panel (window.banxa.showConversionDebugPanel)
// All DD RUM actions are buffered in non-production whenever the panel is closed or open;
// opening the panel only toggles visibility. Not available in production; arbitrary addAction
// payloads may still contain sensitive fields.
// -----------------------------------------------------------------------------
const CONVERSION_DEBUG_MAX = 100;
const CONVERSION_DEBUG_STORAGE_KEY = 'dd.conversion.debug.entries';

export const conversionDebugVisible = ref(false);
export const conversionDebugEntries = ref([]);

const persistConversionDebugEntries = () => {
  if (isProduction || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      CONVERSION_DEBUG_STORAGE_KEY,
      JSON.stringify(conversionDebugEntries.value),
    );
  } catch {
    // ignore quota / private mode
  }
};

const loadConversionDebugEntriesFromStorage = () => {
  if (isProduction || typeof sessionStorage === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(CONVERSION_DEBUG_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    const valid = parsed.filter(
      (row) => row && typeof row.ts === 'number' && typeof row.actionName === 'string',
    );
    conversionDebugEntries.value = valid.slice(-CONVERSION_DEBUG_MAX);
  } catch {
    // ignore corrupt JSON
  }
};

loadConversionDebugEntriesFromStorage();

export const clearConversionDebugEntries = () => {
  conversionDebugEntries.value = [];
  if (!isProduction && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(CONVERSION_DEBUG_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};

const pushConversionDebugEntry = (actionName, payload) => {
  if (isProduction) return;
  try {
    const safePayload = sanitize(payload && typeof payload === 'object' ? payload : {});
    const entry = {
      ts: Date.now(),
      iso: new Date().toISOString(),
      actionName,
      payload: safePayload,
    };
    const arr = [...conversionDebugEntries.value, entry];
    conversionDebugEntries.value = arr.length > CONVERSION_DEBUG_MAX
      ? arr.slice(-CONVERSION_DEBUG_MAX)
      : arr;
    persistConversionDebugEntries();
  } catch {
    // ignore
  }
};

const installBanxaConversionDebug = () => {
  if (typeof window === 'undefined' || isProduction) return;
  try {
    window.banxa = window.banxa || {};
    const desc = Object.getOwnPropertyDescriptor(window.banxa, 'showConversionDebugPanel');
    if (desc?.set && desc?.get) return;

    Object.defineProperty(window.banxa, 'showConversionDebugPanel', {
      configurable: true,
      enumerable: true,
      get() {
        return conversionDebugVisible.value;
      },
      set(v) {
        conversionDebugVisible.value = Boolean(v);
      },
    });
  } catch {
    // ignore
  }
};

installBanxaConversionDebug();

export const setGlobalContextProperty = (key, payload) => {
  try {
    window.DD_RUM?.setGlobalContextProperty(key, payload);
  } catch {
    // fail silent
  }
};

export const readGlobalContextProperty = (key) => {
  try {
    return window.DD_RUM?.getGlobalContext?.()?.[key];
  } catch {
    return null;
  }
};

export const removeGlobalContextProperty = (key) => {
  try {
    window.DD_RUM?.removeGlobalContextProperty(key);
  } catch {
    // fail silent
  }
};

export const setViewContextProperty = (key, value) => {
  try {
    window.DD_RUM?.setViewContextProperty(key, value);
  } catch {
    // fail silent
  }
};

export const setUser = (payload = {}) => {
  try {
    window.DD_RUM?.setUser(payload);
  } catch {
    // fail silent
  }
};

export const addAction = (actionName = '', payload = {}) => {
  try {
    safeLog(actionName, payload);
    if (!isProduction) {
      pushConversionDebugEntry(actionName, payload);
    }
    window.DD_RUM?.addAction(actionName, payload);
  } catch {
    // fail silent
  }
};

// Send sequence so the server can restore client-side dispatch order even
// when concurrent /track POSTs arrive out of order.
let conversionEventSeq = 0;

const stampConversionEvent = (payload) => {
  const base = payload && typeof payload === 'object' ? payload : {};
  conversionEventSeq += 1;
  return {
    ...base,
    client_ts: Date.now(),
    client_seq: conversionEventSeq,
  };
};

const emitConversionEvent = (actionName, payload) => {
  const timestampedPayload = stampConversionEvent(payload);
  addAction(actionName, timestampedPayload);
  sendConversionTrackEvent(actionName, timestampedPayload);
};

export const addFeatureFlagEvaluation = (featureName = '', value = true) => {
  try {
    window.DD_RUM?.addFeatureFlagEvaluation(featureName, value);
  } catch {
    // fail silent
  }
};

export const startView = ({ name }) => {
  try {
    window.DD_RUM?.startView?.({ name });
  } catch {
    // fail silent
  }
};

// -----------------------------------------------------------------------------
// Conversion taxonomy (dotted event keys — frontend sheet + FE-capable submit events)
// -----------------------------------------------------------------------------

export const CONVERSION_EVENTS = Object.freeze({
  ORDER_FLOW_STARTED: 'order.flow.started',
  ORDER_SUBMIT_ATTEMPTED: 'order.submit.attempted',
  ORDER_FLOW_ENDED: 'order.flow.ended',
  VERIFICATION_FLOW_STARTED: 'verification.flow.started',
  VERIFICATION_METHOD_SELECTED: 'verification.method.selected',
  VERIFICATION_MARKETING_OPTED_IN: 'verification.marketing.opted_in',
  VERIFICATION_EMAIL_CHANGED: 'verification.email.changed',
  VERIFICATION_OTP_REQUESTED: 'verification.otp.requested',
  VERIFICATION_OTP_RE_REQUESTED: 'verification.otp.re_requested',
  VERIFICATION_OTP_SUBMITTED: 'verification.otp.submitted',
  VERIFICATION_SOCIAL_REQUESTED: 'verification.social.requested',
  VERIFICATION_SUBMIT_FAILED: 'verification.submit.failed',
  VERIFICATION_SUBMIT_COMPLETED: 'verification.submit.completed',
  VERIFICATION_FLOW_ENDED: 'verification.flow.ended',
  IDENTITY_FLOW_STARTED: 'identity.flow.started',
  IDENTITY_STEP_STARTED: 'identity.step.started',
  IDENTITY_STEP_LEFT: 'identity.step.left',
  IDENTITY_STEP_COMPLETED: 'identity.step.completed',
  IDENTITY_PROVIDER_LOADED: 'identity.provider.loaded',
  IDENTITY_PROVIDER_SUBMITTED: 'identity.provider.submitted',
  IDENTITY_QUEUE_FETCHED: 'identity.queue.fetched',
  IDENTITY_FLOW_ENDED: 'identity.flow.ended',
  PAYMENT_FLOW_STARTED: 'payment.flow.started',
  PAYMENT_PAGE_PRESENTED: 'payment.page.presented',
  PAYMENT_SUBMIT_ATTEMPTED: 'payment.submit.attempted',
  PAYMENT_3DS_CHALLENGED: 'payment.3ds.challenged',
  PAYMENT_PROVIDER_NOTIFICATION: 'payment.provider.notification',
  PAYMENT_PROVIDER_ERROR: 'payment.provider.error',
  PAYMENT_FLOW_ENDED: 'payment.flow.ended',
  STATUS_FLOW_STARTED: 'status.flow.started',
  STATUS_PAGE_VIEWED: 'status.page.viewed',
});

/**
 * @deprecated Prefer CONVERSION_EVENTS — legacy keys now map to taxonomy strings (not checkout.*).
 */
export const CONVERSION_ACTIONS = Object.freeze({
  FLOW_STARTED: CONVERSION_EVENTS.ORDER_FLOW_STARTED,
  STEP_COMPLETED: CONVERSION_EVENTS.IDENTITY_STEP_COMPLETED,
  FLOW_EXITED: CONVERSION_EVENTS.PAYMENT_FLOW_ENDED,
  PAYMENT_STARTED: CONVERSION_EVENTS.PAYMENT_FLOW_STARTED,
  PAYMENT_COMPLETED: CONVERSION_EVENTS.PAYMENT_FLOW_ENDED,
  STATUS_VIEWED: CONVERSION_EVENTS.STATUS_PAGE_VIEWED,
  VERIFICATION_FAILED: CONVERSION_EVENTS.VERIFICATION_SUBMIT_FAILED,
  VERIFICATION_STARTED: CONVERSION_EVENTS.VERIFICATION_FLOW_STARTED,
  VERIFICATION_ATTEMPTED: CONVERSION_EVENTS.VERIFICATION_OTP_SUBMITTED,
  IDENTITY_STARTED: CONVERSION_EVENTS.IDENTITY_FLOW_STARTED,
  IDENTITY_ERROR: CONVERSION_EVENTS.IDENTITY_PROVIDER_SUBMITTED,
  PAYMENT_PAGE_VIEWED: CONVERSION_EVENTS.PAYMENT_PAGE_PRESENTED,
  PAYMENT_ERROR: CONVERSION_EVENTS.PAYMENT_FLOW_ENDED,
});

export const CHECKOUT_STEPS = Object.freeze({
  ORDER_PLACED: 'order_placed',
  // Main verification step; OTP is a `verification_substep` (see VERIFICATION_SUBSTEPS)
  VERIFICATION_COMPLETED: 'verification_completed',
  IDENTITY_COMPLETED: 'identity_completed',
  PAYMENT_COMPLETED: 'payment_completed',
  STATUS_VIEWED: 'status_viewed',
  VERIFICATION: 'verification',
  IDENTITY_SUBMISSION: 'identity_submission', // When they submit to sumsub/plaid
  IDENTITY_DETAILS: 'identity_details', // Micro-step context (e.g., personal details page)
  PAYMENT_METHOD: 'payment_method',
});

// Substeps for the verification phase (e.g. OTP code entry)
export const VERIFICATION_SUBSTEPS = Object.freeze({
  OTP: 'otp',
});

const CHECKOUT_FLOW_KEY = 'dd.checkout.flow';

const uuid = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

const detectPlatform = () => {
  if (typeof window !== 'undefined' && window?.Capacitor) return 'mobile';
  return 'web';
};

export const initCheckoutFlow = ({
  entryPoint = 'unknown',
  flowVersion = 'unknown',
  paymentMethod = 'unknown',
  partner = 'direct',
  platform,
  extra = {},
} = {}) => {
  try {
    const flow = sanitize({
      flow_id: uuid(),
      flow_name: 'checkout',
      flow_version: flowVersion,
      entry_point: entryPoint,
      platform: platform || detectPlatform(),
      payment_method: paymentMethod,
      partner,
      ...extra,
    });

    try {
      sessionStorage.setItem(CHECKOUT_FLOW_KEY, JSON.stringify(flow));
    } catch {
      // ignore storage failures
    }

    setGlobalContextProperty('flow', flow);
    emitConversionEvent(CONVERSION_EVENTS.ORDER_FLOW_STARTED, flow);

    return flow;
  } catch {
    return null;
  }
};

export const getCheckoutFlow = () => {
  try {
    const ctxFlow = readGlobalContextProperty('flow');
    if (ctxFlow && ctxFlow.flow_id) return ctxFlow;

    const raw = sessionStorage.getItem(CHECKOUT_FLOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.flow_id ? parsed : null;
  } catch {
    return null;
  }
};

// Dedupe: only send each step once per flow (flow_id:step_name).
// Persisted to sessionStorage so dedupe survives PAPI transit SPA reloads
// (3DS challenges, payment provider handoffs, etc. all reload the SPA on return).
const SENT_STEP_KEYS_STORAGE_KEY = 'dd.checkout.sent_step_keys';

const loadSentStepKeysFromStorage = () => {
  if (typeof sessionStorage === 'undefined') return new Set();
  try {
    const raw = sessionStorage.getItem(SENT_STEP_KEYS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((k) => typeof k === 'string'));
  } catch {
    return new Set();
  }
};

const sentStepKeys = loadSentStepKeysFromStorage();

const persistSentStepKeys = () => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      SENT_STEP_KEYS_STORAGE_KEY,
      JSON.stringify([...sentStepKeys]),
    );
  } catch {
    // ignore quota / private mode
  }
};

export const ensureCheckoutFlowStarted = ({
  entryPoint = 'unknown',
  flowVersion = 'unknown',
  paymentMethod = 'unknown',
  partner = 'direct',
  platform,
  extra = {},
} = {}) => {
  try {
    const existing = getCheckoutFlow();
    if (existing?.flow_id && existing?.entry_point === entryPoint) {
      return existing;
    }
    return initCheckoutFlow({
      entryPoint,
      flowVersion,
      paymentMethod,
      partner,
      platform,
      extra,
    });
  } catch {
    return null;
  }
};

export const initPapiTransitCheckoutForRoute = (route) => {
  if (route?.name !== 'order.papi') {
    return null;
  }
  const orderStore = useOrderFormStore();
  return ensureCheckoutFlowStarted({
    entryPoint: 'papi_transit',
    paymentMethod: orderStore.getPaymentObject?.pspTypeCode ?? 'unknown',
    flowVersion: '1.0',
  });
};

const stepKey = (flowId, stepName) => (flowId && stepName ? `${flowId}:${stepName}` : null);

// Merge flow context + extra, sanitize, optional once-per-flow dedupe, emit RUM action.
// Returns whether it ran.
const sendFlowAction = (ddAction, extra = {}, dedupeKey = null) => {
  try {
    const flow = getCheckoutFlow() || {};
    if (dedupeKey) {
      const key = stepKey(flow?.flow_id, dedupeKey);
      if (key && sentStepKeys.has(key)) {
        return false;
      }
      if (key) {
        sentStepKeys.add(key);
        persistSentStepKeys();
      }
    }
    const orderPaymentMethod = getOrderData()?.paymentMethod;
    const paymentMethod = orderPaymentMethod
      ? String(orderPaymentMethod).toLowerCase()
      : flow.payment_method;
    const payload = sanitize({ ...flow, payment_method: paymentMethod, ...extra });
    emitConversionEvent(ddAction, payload);
    return true;
  } catch {
    return false;
  }
};

export const clearCheckoutFlow = () => {
  try {
    const flow = getCheckoutFlow();
    if (flow?.flow_id) {
      [...sentStepKeys].forEach((key) => {
        if (key.startsWith(`${flow.flow_id}:`)) {
          sentStepKeys.delete(key);
        }
      });
      persistSentStepKeys();
    }
    sessionStorage.removeItem(CHECKOUT_FLOW_KEY);
  } catch {
    // ignore
  }
  removeGlobalContextProperty('flow');
};

/**
 * @deprecated Prefer specific track* helpers; emits identity.step.completed.
 */
export const trackCheckoutStep = ({
  stepName,
  stepIndex,
  extra = {},
} = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.IDENTITY_STEP_COMPLETED,
    { step_name: stepName, step_index: stepIndex, ...extra },
    stepName,
  );
};

export const trackOrderPlaced = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.ORDER_FLOW_ENDED,
    { transition_from: 'order', ...extra },
    CONVERSION_EVENTS.ORDER_FLOW_ENDED,
  );
};

const defaultVerificationSubstep = (extra) => (
  extra?.skipped ? {} : { verification_substep: VERIFICATION_SUBSTEPS.OTP }
);

export const trackVerificationCompleted = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.VERIFICATION_SUBMIT_COMPLETED,
    {
      step_name: CHECKOUT_STEPS.VERIFICATION_COMPLETED,
      step_index: 2,
      ...defaultVerificationSubstep(extra),
      ...extra,
    },
    CONVERSION_EVENTS.VERIFICATION_SUBMIT_COMPLETED,
  );
};

export const trackVerificationFailed = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_SUBMIT_FAILED, {
    verification_substep: VERIFICATION_SUBSTEPS.OTP,
    ...extra,
  });
};

export const trackVerificationStarted = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.VERIFICATION_FLOW_STARTED,
    { verification_substep: VERIFICATION_SUBSTEPS.OTP, ...extra },
    CONVERSION_EVENTS.VERIFICATION_FLOW_STARTED,
  );
};

export const trackVerificationAttempted = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_OTP_SUBMITTED, {
    verification_substep: VERIFICATION_SUBSTEPS.OTP,
    ...extra,
  });
};

export const trackIdentityCompleted = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.IDENTITY_STEP_COMPLETED,
    {
      step_name: CHECKOUT_STEPS.IDENTITY_COMPLETED,
      step_index: 3,
      ...extra,
    },
    `${CONVERSION_EVENTS.IDENTITY_STEP_COMPLETED}:${CHECKOUT_STEPS.IDENTITY_COMPLETED}`,
  );
};

export const trackIdentityStarted = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.IDENTITY_FLOW_STARTED,
    extra,
    CONVERSION_EVENTS.IDENTITY_FLOW_STARTED,
  );
};

export const trackIdentityError = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_PROVIDER_SUBMITTED, { outcome: 'error', ...extra });
};

export const trackPaymentStarted = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.PAYMENT_FLOW_STARTED,
    extra,
    CONVERSION_EVENTS.PAYMENT_FLOW_STARTED,
  );
};

export const trackPaymentPageViewed = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.PAYMENT_PAGE_PRESENTED,
    extra,
    CONVERSION_EVENTS.PAYMENT_PAGE_PRESENTED,
  );
};

export const trackPaymentError = (extra = {}) => {
  sendFlowAction(
    CONVERSION_EVENTS.PAYMENT_FLOW_ENDED,
    { outcome: 'error', ...extra },
    `${CONVERSION_EVENTS.PAYMENT_FLOW_ENDED}:error`,
  );
};

export const trackPaymentCompleted = (extra = {}) => {
  try {
    if (sendFlowAction(
      CONVERSION_EVENTS.PAYMENT_FLOW_ENDED,
      {
        outcome: 'success',
        step_name: CHECKOUT_STEPS.PAYMENT_COMPLETED,
        step_index: 4,
        ...extra,
      },
      `${CONVERSION_EVENTS.PAYMENT_FLOW_ENDED}:success`,
    )) {
      clearCheckoutFlow();
    }
  } catch {
    // fail silent
  }
};

export const trackStatusViewed = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.STATUS_FLOW_STARTED, {}, CONVERSION_EVENTS.STATUS_FLOW_STARTED);
  sendFlowAction(CONVERSION_EVENTS.STATUS_PAGE_VIEWED, {
    step_name: CHECKOUT_STEPS.STATUS_VIEWED,
    step_index: 5,
    ...extra,
  });
};

export const trackFlowExit = (exitReason, extra = {}) => {
  try {
    if (sendFlowAction(CONVERSION_EVENTS.PAYMENT_FLOW_ENDED, {
      exit_reason: exitReason ?? 'unknown',
      ...extra,
    })) {
      clearCheckoutFlow();
    }
  } catch {
    // fail silent
  }
};

/** Thin exports for sheet events not yet wired (merge checkout flow context). */
export const trackOrderSubmitAttempted = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.ORDER_SUBMIT_ATTEMPTED, extra);
};

export const trackVerificationMethodSelected = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_METHOD_SELECTED, extra);
};

export const trackVerificationMarketingOptedIn = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_MARKETING_OPTED_IN, extra);
};

export const trackVerificationEmailChanged = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_EMAIL_CHANGED, extra);
};

export const trackVerificationOtpRequested = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_OTP_REQUESTED, extra);
};

export const trackVerificationOtpReRequested = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_OTP_RE_REQUESTED, extra);
};

export const trackVerificationSocialRequested = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_SOCIAL_REQUESTED, extra);
};

export const trackVerificationFlowEnded = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.VERIFICATION_FLOW_ENDED, extra);
};

export const trackIdentityProviderLoaded = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_PROVIDER_LOADED, extra);
};

export const trackIdentityProviderSubmitted = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_PROVIDER_SUBMITTED, extra);
};

/**
 * Snapshot of `/kyc/queue` for timeline analysis. Intentionally NOT deduped —
 * every fetch should emit so the queue can be reconstructed over time.
 */
export const trackIdentityQueueFetched = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_QUEUE_FETCHED, extra);
};

/**
 * Fired on entering an identity step (route name marked `meta.identityStep`).
 * Intentionally NOT deduped — re-entries (back nav, retries) are meaningful.
 */
export const trackIdentityStepStarted = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_STEP_STARTED, extra);
};

/**
 * Fired on leaving an identity step. Pair with IDENTITY_STEP_STARTED to detect
 * abandonment vs progression by inspecting `to_step` / `to_is_identity`.
 */
export const trackIdentityStepLeft = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_STEP_LEFT, extra);
};

export const trackIdentityFlowEnded = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.IDENTITY_FLOW_ENDED, extra);
};

export const trackPaymentSubmitAttempted = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.PAYMENT_SUBMIT_ATTEMPTED, extra);
};

export const trackPayment3dsChallenged = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.PAYMENT_3DS_CHALLENGED, extra);
};

export const trackPaymentProviderNotification = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.PAYMENT_PROVIDER_NOTIFICATION, extra);
};

export const trackPaymentProviderError = (extra = {}) => {
  sendFlowAction(CONVERSION_EVENTS.PAYMENT_PROVIDER_ERROR, extra);
};

export const setExperimentVariant = (experimentName, variant) => {
  try {
    addFeatureFlagEvaluation(experimentName, variant);

    const flow = getCheckoutFlow();
    if (flow) {
      const nextFlow = {
        ...flow,
        experiment: { ...(flow.experiment || {}), [experimentName]: variant },
      };
      try {
        sessionStorage.setItem(CHECKOUT_FLOW_KEY, JSON.stringify(nextFlow));
      } catch {
        // ignore
      }
      setGlobalContextProperty('flow', nextFlow);
    }
  } catch {
    // fail silent
  }
};

export const addDataDogError = (error) => {
  if (import.meta.env.VITE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  window.DD_RUM?.addError(error);
};
